import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { Script } from 'gatsby';
import { injectIntl } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import videoShape from '@prospectus/common/types/videoShape';
import courseRunShape from '@prospectus/common/types/courseRunShape';
import {
  getFullName,
  getListFromRawHTML,
  getInstructorPositionOrg,
} from '@utils/formatting';
import {
  getFullMarketingUrl,
  getEducationalLevel,
  getMappedCourseInstances,
  getShortenedLanguageCode,
  getTruncatedDescription,
  getOffers,
  getEducationalCredentialAwarded,
  getAggregateRating,
} from '@utils/course';
import { publisher } from '@utils/seo';

export const getCourseSchemaOrgJsonLD = ({
  course, activeCourseRun, imageUrl, intl, hasCertificate,
}) => {
  const { staff = [] } = activeCourseRun;
  const instructorMap = staff.map(instructor => ({
    '@type': 'Person',
    name: getFullName(instructor),
    description: getInstructorPositionOrg(instructor.position),
    image: instructor.profileImageUrl,
  }));
  const language = getShortenedLanguageCode(activeCourseRun?.contentLanguage);

  const json = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': getFullMarketingUrl(course),
    name: course.title,
    courseCode: activeCourseRun.course,
    offers: getOffers(course),
    url: getFullMarketingUrl(course, intl.locale),
    about: course.skillNames,
    ...(hasCertificate ? { educationalCredentialAwarded: getEducationalCredentialAwarded(course) } : ''),
    educationalLevel: getEducationalLevel(course.levelType),
    isAccessibleForFree: course.isFreeOption,
    inLanguage: language,
    availableLanguage: language,
    timeRequired: `W${activeCourseRun.weeksToComplete}`,
    description: course.strippedShortDescription
      ? getTruncatedDescription(course.strippedShortDescription)
      : getTruncatedDescription(course.shortDescription),
    provider: course.owners.map(owner => ({
      '@type': 'CollegeOrUniversity',
      name: owner.name,
      url: owner.marketingUrl,
    })),
    hasCourseInstance: getMappedCourseInstances(course, instructorMap),
    publisher,
    image: [imageUrl],
    teaches: getListFromRawHTML(course.outcome),
    totalHistoricalEnrollment: course.enrollmentCount,
    coursePrerequisites: getListFromRawHTML(course.prerequisitesRaw),
  };

  if (course.courseReview) {
    json.aggregateRating = getAggregateRating(course.courseReview);
  }

  if (course.video) {
    json.video = {
      '@type': 'VideoObject',
      name: course.title,
      description: course.video.description,
      contentUrl: course.video.src,
      thumbnailUrl: course.video.image?.src,
    };
  }
  return json;
};

/**
 * Return course-specific meta data to augment the general page metadata
 * @param {course} the course being rendered
 * @param {imageUrl} the image for this course
 * @returns Helmet code that contains the metadata
 */
function getCustomMetadata({ course, imageUrl }) {
  const videoUrl = course.video ? course.video.src : '';

  return (
    <Helmet>
      <meta property="og:image:secure_url" content={imageUrl} key="og:image:secure_url" />
      <meta property="og:video:url" content={videoUrl} key="og:video:url" />
      <meta property="og:video:width" content="1280" key="og:video:width" />
      <meta property="og:video:height" content="720" key="og:video:height" />
      <meta property="og:video:type" content="application/x-shockwave-flash" key="og:video:type" />
    </Helmet>
  );
}

getCustomMetadata.propTypes = {
  course: PropTypes.shape({
    video: videoShape,
  }).isRequired,
  imageUrl: PropTypes.string.isRequired,
};

/**
 * Return schema.org JSON-LD for a course detail page
 * @param {course} the course being displayed on the page
 * @param {activeCourseRun} the active run of the course
 * @param {imageUrl} the image for this course
 * @returns Helmet code that renders the appropriate course JsonLD information
 */
function SEOCourse({
  course, activeCourseRun, imageUrl, intl, hasCertificate,
}) {
  const customMetadata = getCustomMetadata({ course, imageUrl });

  const courseJsonLD = getCourseSchemaOrgJsonLD({
    course, activeCourseRun, imageUrl, intl, hasCertificate,
  });

  return (
    <>
      {customMetadata}
      <Script type="application/ld+json">{JSON.stringify(courseJsonLD)}</Script>
    </>
  );
}

SEOCourse.propTypes = {
  course: PropTypes.shape({
    title: PropTypes.string.isRequired,
    shortDescription: PropTypes.string,
    strippedShortDescription: PropTypes.string,
    isFreeOption: PropTypes.bool.isRequired,
    uuid: PropTypes.string.isRequired,
    enrollmentCount: PropTypes.number.isRequired,
    levelType: PropTypes.oneOf(['Introductory', 'Intermediate', 'Advanced']).isRequired,
    outcome: PropTypes.string,
    owners: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      marketingUrl: PropTypes.string.isRequired,
    })).isRequired,
    prerequisitesRaw: PropTypes.string,
    video: PropTypes.shape({
      description: PropTypes.string,
      src: PropTypes.string,
      image: PropTypes.shape({
        src: PropTypes.string,
      }),
    }),
    skillNames: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  activeCourseRun: PropTypes.shape(courseRunShape).isRequired,
  imageUrl: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  hasCertificate: PropTypes.bool.isRequired,
};

export default injectIntl(SEOCourse);
